import { useState, useEffect, useCallback, useRef } from 'react'
import { useModal } from '@hooks/useModal'
import { useTimeoutLogout } from '@hooks/useLogout'
import { IDLE_TIMEOUT_SIGNED_OUT_MESSAGE } from '@constants'

interface UseIdleTimeoutOptions {
  idleTimeoutMinutes?: number
  warningTimeoutMinutes?: number
  countdownSeconds?: number
}

export const useIdleTimeout = ({
  idleTimeoutMinutes = 15,
  warningTimeoutMinutes = 2,
  countdownSeconds = 2 * 60, // 2 minutes in seconds
}: UseIdleTimeoutOptions = {}) => {
  const { openSignInModal } = useModal({ modalID: 'idle-timeout-login-modal' })
  const { handleTimeoutLogout } = useTimeoutLogout()
  const [showWarning, setShowWarning] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null)

  const resetTimers = useCallback(() => {
    setLastActivity(Date.now())
    setShowWarning(false)

    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
    }

    // Set new timers
    const warningTimeout = warningTimeoutMinutes * 60 * 1000
    const idleTimeout = idleTimeoutMinutes * 60 * 1000

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true)
    }, warningTimeout)

    idleTimerRef.current = setTimeout(() => {
      handleTimeoutLogout()
    }, idleTimeout)
  }, [handleTimeoutLogout, idleTimeoutMinutes, warningTimeoutMinutes])

  const handleActivity = useCallback(() => {
    if (!showWarning) {
      resetTimers()
    }
  }, [showWarning, resetTimers])

  const handleStaySignedIn = useCallback(() => {
    setShowWarning(false)
    resetTimers()
  }, [resetTimers])

  const handleSignOut = useCallback(async () => {
    // Use handleTimeoutLogout instead of handleLogout to avoid snackbar and handle failures gracefully
    try {
      await handleTimeoutLogout()
      // Open sign-in modal with inactivity message after successful logout
      openSignInModal(undefined, IDLE_TIMEOUT_SIGNED_OUT_MESSAGE)
    } catch (error) {
      console.error('Idle timeout logout failed:', error)
      // Force close the warning modal even if logout fails
      setShowWarning(false)
    }
  }, [handleTimeoutLogout, openSignInModal])

  // Set up activity listeners
  useEffect(() => {
    const events = ['keypress', 'click', 'touchstart', 'mousemove', 'mousedown']

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true)
    })

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
      })
    }
  }, [handleActivity])

  // Initialize timers on mount
  useEffect(() => {
    resetTimers()

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current)
      }
    }
  }, [resetTimers])

  return {
    showWarning,
    handleStaySignedIn,
    handleSignOut,
    countdownSeconds,
  }
}
